import { RadioGroup } from "components/Atoms/inputs/RadioGroup";
import { SectionFieldEditType } from "components/MainSectionTemplate/types";
import React from "react";
import FFInPageEditOptionalDatePicker from "./FFInPageEditOptionalDatePicker";
import FFInPageEditOptionalDateTimePicker from "./FFInPageEditOptionalDateTimePicker";
import OptionalNumberInput from "components/ValidatedFields/OptionalNumberInput";
import BaseReactSelectDropdown from "components/dropdowns/reactselect/BaseReactSelectDropdown";
import PercentageInput from "components/PercentageInput";
import OptionalTextAreaInput from "components/ValidatedFields/OptionalTextAreaInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import FFInPageEditOptionalTimePicker from "./FFInPageEditOptionalTimePicker";
import UrlInput from "components/urlinput";
import EmailInput from "components/ValidatedFields/WithErrorLabel/EmailInput";

interface Props {
  type: server.dto.FormElementTypes
  dropdownOptions?: string[]
  reference: string
  value: string
  onChange: (value: string, submit?: boolean) => void
  submit: () => void
  cancel: () => void
}

export const FFInPageEditInput = (props: Props) => {
  const { type } = props;

  if (type === "dropdown" && !props.dropdownOptions) throw new Error("Dropdown freefield must have dropdownItems");

  const onChangeNumber = (value: number, submit?: boolean) => {
    const str = value?.toString();
    if (!str || str === "NaN" || str === "") {
      props.onChange("", submit);
    } else {
      props.onChange(str, submit);
    }
  }

  switch (type) {
    case "boolean":
      return <div style={{ paddingTop: 6, paddingLeft: 8 }}>
        <RadioGroup
          orientation="horizontal"
          options={[
            { label: RESX.GeneralLabels.lblYes, value: "true" },
            { label: RESX.GeneralLabels.lblNo, value: "false" }
          ]}
          value={props.value}
          onChange={v => props.onChange(v, true)}
        />
      </div>
    case "booleanNullable":
      return <div style={{ paddingTop: 6, paddingLeft: 8 }}>
        <RadioGroup
          orientation="horizontal"
          options={[
            { label: RESX.GeneralLabels.lblYes, value: "true" },
            { label: RESX.GeneralLabels.lblNo, value: "false" },
            { label: RESX.GeneralLabels.NoChoiceMade, value: "" }
          ]}
          value={props.value}
          onChange={v => props.onChange(v, true)}
        />
      </div>
    case "date":
      return <FFInPageEditOptionalDatePicker
        value={props.value}
        onChange={props.onChange}
        reference={props.reference}
        submit={props.submit}
        cancel={props.cancel}
      />;
    case "dateTime":
      return <FFInPageEditOptionalDateTimePicker
        value={props.value}
        onChange={props.onChange}
        reference={props.reference}
        submit={props.submit}
        cancel={props.cancel}
      />
    case "decimal":
      return <OptionalNumberInput
        canBeNegative
        canBeEmpty
        autoFocus
        width={140}
        value={parseFloat(props.value) || 0}
        onChange={(v: number) => onChangeNumber(v)}
        onEnterPressed={(v: number) => onChangeNumber(v, true)}
        onEscPressed={() => props.cancel()}
        decimalPoints={2}
      />

    case "dropdown":
      return <BaseReactSelectDropdown
        excludeKeys={[]}
        options={props.dropdownOptions.map(o => ({ value: o, label: o }))}
        getLabel={v => v.label}
        getValue={v => v.value}
        value={{ value: props.value, label: props.value }}
        onChange={v => props.onChange(v.value, true)}
        onEsc={() => props.cancel()}
        config={{
          focusOnMount: true,
          openMenuOnFocus: true,
        }}
      />
    case "email":
      return <EmailInput
        focusOnMount
        validatorKey={`freefield${props.reference}`}
        value={props.value}
        onChange={props.onChange}
        onEnter={() => props.submit()}
        onEsc={() => props.cancel()}
        emailCanBeBlank
      />;
    case "integer":
      return <OptionalNumberInput
        autoFocus
        canBeNegative
        canBeEmpty
        width={140}
        value={parseFloat(props.value) || 0}
        onChange={(v: number) => onChangeNumber(v)}
        onEnterPressed={(v: number) => onChangeNumber(v, true)}
        onEscPressed={() => props.cancel()}
        decimalPoints={0}
      />
    case "percentage":
      return <PercentageInput
        autoFocus
        onEnter={v => onChangeNumber(v * 100, true)}
        onEsc={props.cancel}
        canBeNegative={false}
        digitsAfterDecimalPoint={2}
        enablePercentageLabel
        value={parseFloat(props.value) / 100 || 0}
        onChange={v => onChangeNumber(v * 100)}
      />
    case "textMultiLine":
      return <OptionalTextAreaInput
        focusOnMount
        maxLength={serverReference.constants.freeField.maxMultiLineTextLength}
        value={props.value}
        onChange={(v) => { props.onChange(v); }}
        onCtrlEnter={() => props.submit()}
        onEsc={() => props.cancel()}
      />
    case "textSingleLine":
      return <OptionalTextInput
        focusOnMount
        maxLength={serverReference.constants.freeField.maxSingleLineTextLength}
        value={props.value}
        onChange={(v) => { props.onChange(v); }}
        onEnter={() => props.submit()}
        onEsc={() => props.cancel()}
      />
    case "time":
      return <FFInPageEditOptionalTimePicker
        value={props.value}
        onChange={props.onChange}
        reference={props.reference}
        submit={props.submit}
        cancel={props.cancel}
      />
    case "url":
      return <UrlInput
        focusOnMount
        value={props.value}
        onValueChange={props.onChange}
        validatorKey={`freefield${props.reference}`}
        onEnter={props.submit}
        onEsc={props.cancel}
        maxLength={serverReference.constants.url.maxUrlLength}
      />
    default: throw new Error("Unknown freefield definition type: " + type);
  }
};

export const getInPageEditType = (type: server.dto.FormElementTypes): SectionFieldEditType => {
  if (type === "dropdown" || type === "boolean" || type === "booleanNullable") {
    return "custom";
  }

  if (type === "date" || type === "dateTime" || type === "time") {
    return "confirmOrCancelButtonInline";
  }

  return "confirmOrCancelButton";
}

export const getInPageEditFullWidth = (type: server.dto.FormElementTypes): boolean => {
  if (type === "date" || type === "dateTime" || type === "time" || type === "decimal" || type === "integer" || type === "percentage") {
    return false;
  }
  return true;
}